import React, { useState, useEffect, useCallback, useRef } from 'react';
import './styles/tailwind.css';
import './App.css';
import './index.css';
import './styles/HandleFreeFlow.css';
import './styles/HandleTimer.css';
import './styles/MusicPlayer.css';
import HandleTimer from './components/HandleTimer';
import MusicPlayer from './components/MusicPlayer';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SessionHistoryPage from './pages/SessionHistoryPage';
import Menu from './components/Menu';
import Profile from './pages/Profile';
import ChangeBackground from './pages/ChangeBackground';
import TrackListPage from './pages/TrackListPage';
import QuoteList from './pages/QuoteList';
import Projects from './pages/Projects';
import FavoritesPage from './pages/FavoritesPage';
import MyImagesPage from './pages/MyImagesPage';
import WeatherPage from './pages/WeatherPage';
import WeatherWidget from './components/WeatherWidget';
import LoginPage from './pages/LoginPage';
import { fetchWeather, getWeatherIconKey, getIsNight } from './services/weatherService';
import { useAuth } from './context/AuthContext';
import { authFetch } from './utils/api';
import { Heart, ArrowsClockwise } from '@phosphor-icons/react';
import { fetchFavorites, addFavorite, deleteFavorite } from './services/favoritesService';
import sfxManager from './utils/sfxManager';
import useSpinAction from './utils/useSpinAction';

function App() {
  const { isAuthenticated, isLoading } = useAuth();
  const [isFreeflow, setIsFreeflow] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [showMusicPlayer, setShowMusicPlayer] = useState(false);
  // Timer ledger: timestamps in refs (don't drive re-renders directly), tick state forces re-render
  const sessionStartTsRef = useRef(null);
  const accumulatedSecondsRef = useRef(0);
  const lastResumeAtRef = useRef(null);
  const prevTimerActiveRef = useRef(false);
  const isTransitioningRef = useRef(false);
  const lastStorageWriteRef = useRef(0);
  const [, setTick] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  // Derived elapsed seconds — recomputed on every render
  const time = lastResumeAtRef.current === null
    ? accumulatedSecondsRef.current
    : accumulatedSecondsRef.current + Math.floor((Date.now() - lastResumeAtRef.current) / 1000);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [currentSessionText, setCurrentSessionText] = useState('');
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('userVolume');
    return saved !== null ? parseFloat(saved) : 0.5;
  });
  const [showSessionHistory, setShowSessionHistory] = useState(false);
  const [totalFocusedTime, setTotalFocusedTime] = useState(0);
  const [isSessionHistoryExiting, setIsSessionHistoryExiting] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState('');
  const [backgroundImageReady, setBackgroundImageReady] = useState(false);
  const [photo, setPhoto] = useState({});
  const [photographer, setPhotographer] = useState({
    name: '',
    username: '',
    link: '',
    photoLink: ''
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [unsplashTheme, setUnsplashTheme] = useState('favorites');
  const [showProfile, setShowProfile] = useState(false);
  const [showChangeBackgroundImage, setShowChangeBackgroundImage] = useState(false);
  const [isChangeBackgroundImageExiting, setIsChangeBackgroundImageExiting] = useState(false);
  const [showTrackList, setShowTrackList] = useState(false);
  const [isTrackListExiting, setIsTrackListExiting] = useState(false);
  const [playlistTracks, setPlaylistTracks] = useState(() => {
    const saved = localStorage.getItem('focusPlaylist');
    const parsed = saved ? JSON.parse(saved) : [];
    return parsed.length > 0 ? parsed : [/* default track */];
  });
  const [sessionInputValue, setSessionInputValue] = useState('');
  const [sessionStarted, setSessionStarted] = useState(false);
  const [showQuoteList, setShowQuoteList] = useState(false);
  const [isQuoteListExiting, setIsQuoteListExiting] = useState(false);
  const [showProjects, setShowProjects] = useState(false);
  const [isProjectsExiting, setIsProjectsExiting] = useState(false);
  const [showFavoritesPage, setShowFavoritesPage] = useState(false);
  const [isFavoritesPageExiting, setIsFavoritesPageExiting] = useState(false);
  const [showMyImagesPage, setShowMyImagesPage] = useState(false);
  const [isMyImagesPageExiting, setIsMyImagesPageExiting] = useState(false);
  const [showWeatherPage, setShowWeatherPage] = useState(false);
  const [isWeatherPageExiting, setIsWeatherPageExiting] = useState(false);
  const [weatherLocations, setWeatherLocations] = useState(() => {
    try {
      const saved = localStorage.getItem('weatherLocations');
      if (saved) return JSON.parse(saved) || [];
      // migrate from single-location storage
      const old = localStorage.getItem('weatherLocation');
      if (old) { const p = JSON.parse(old); if (p?.lat) return [p]; }
      return [];
    } catch { return []; }
  });
  const [weatherUnit, setWeatherUnit] = useState(() => localStorage.getItem('weatherUnit') || 'F');
  const [weatherDataList, setWeatherDataList] = useState([]);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoritedId, setFavoritedId] = useState(null);
  const [favoritesCache, setFavoritesCache] = useState([]);
  const [backgroundRefreshCooldown, setBackgroundRefreshCooldown] = useState(false);
  const [isRefreshingBackground, refreshBackground] = useSpinAction(
    () => fetchBackgroundImage(unsplashTheme, true),
    1000
  );

  // Reconcile playlist with server on mount: drop stale entries (e.g. tracks
  // uploaded to GCS in a previous session that no longer exist locally in dev),
  // then repopulate from the server if the playlist is empty.
  useEffect(() => {
    const syncPlaylistWithServer = async () => {
      try {
        console.log('Syncing playlist with server...');
        const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/files/list-tracks`);
        if (!response.ok) {
          console.error('Failed to fetch tracks from server');
          return;
        }
        const data = await response.json();
        const serverItems = data.items || [];
        const serverNames = new Set(serverItems.map(i => i.name));

        const stored = JSON.parse(localStorage.getItem('focusPlaylist') || '[]');
        const valid = stored.filter(t => serverNames.has(t.fileName));

        if (valid.length === 0 && serverItems.length > 0) {
          console.log(`Repopulating playlist from ${serverItems.length} server tracks`);
          const formatted = serverItems.map(item => ({
            id: `playlist-${item.name.replace(/[^a-zA-Z0-9]/g, '')}`,
            title: item.name.replace('.mp3', '').replace(/^(test\/|tracks\/)/, ''),
            fileName: item.name
          }));
          setPlaylistTracks(formatted);
          localStorage.setItem('focusPlaylist', JSON.stringify(formatted));
          return;
        }

        if (valid.length !== stored.length) {
          console.log(`Dropping ${stored.length - valid.length} stale playlist entries`);
          setPlaylistTracks(valid);
          localStorage.setItem('focusPlaylist', JSON.stringify(valid));
        }
      } catch (error) {
        console.error('Error syncing playlist with server:', error);
      }
    };

    syncPlaylistWithServer();
  }, [playlistTracks.length]);

  // Add ref to track session state without causing re-renders
  const sessionRef = React.useRef({
    timerActive: false,
    isFreeflow: false,
    time: 0,
    sessionStarted: false,
    sessionEnded: false
  });

  // Update ref when relevant state changes
  useEffect(() => {
    sessionRef.current = {
      timerActive,
      isFreeflow,
      time,
      sessionStarted,
      sessionEnded
    };
  }, [timerActive, isFreeflow, time, sessionStarted, sessionEnded]);

  useEffect(() => {
    const savedHistory = JSON.parse(localStorage.getItem('sessionHistory')) || [];
    setSessionHistory(savedHistory);
  }, []);

  // Tick interval drives UI re-renders at 250ms while the timer is running.
  // It does NOT track elapsed time — that comes from Date.now() in the derived `time` above.
  useEffect(() => {
    if (!timerActive) return;
    const id = setInterval(() => setTick(t => t + 1), 250);
    return () => clearInterval(id);
  }, [timerActive]);

  // Transition handler: maintains the ledger refs across timerActive flips.
  useEffect(() => {
    const wasActive = prevTimerActiveRef.current;
    if (!wasActive && timerActive) {
      // resume / start
      lastResumeAtRef.current = Date.now();
    } else if (wasActive && !timerActive) {
      // pause / stop — accumulate the running span
      if (lastResumeAtRef.current !== null) {
        accumulatedSecondsRef.current += Math.floor((Date.now() - lastResumeAtRef.current) / 1000);
      }
      lastResumeAtRef.current = null;
    }
    prevTimerActiveRef.current = timerActive;
  }, [timerActive]);

  // Utility function to validate if a URL is a valid image
  const isValidImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;

    // Check if URL has valid image extensions
    const imageExtensions = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i;
    const hasImageExtension = imageExtensions.test(url);

    // Check if URL starts with http/https
    const isValidUrl = /^https?:\/\/.+/i.test(url);

    // For URLs without extensions but from known image hosts (like Unsplash)
    const knownImageHosts = [
      'images.unsplash.com',
      'unsplash.com',
      'storage.googleapis.com',
      'images.pexels.com',
      'pixabay.com'
    ];
    const isFromImageHost = knownImageHosts.some(host => url.includes(host));

    return isValidUrl && (hasImageExtension || isFromImageHost);
  };

  // Utility function to test if image URL actually loads
  const testImageLoad = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;

      // Timeout after 5 seconds
      setTimeout(() => resolve(false), 5000);
    });
  };

  const fetchBackgroundImage = async (theme, forceUpdate = false) => {
    try {
      console.log('Fetching fresh background image for theme:', theme);

      if (theme === 'my-images') {
        // Fetch from our GCS bucket
        const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/files/list-images`);
        if (!response.ok) throw new Error('Failed to fetch images');

        const data = await response.json();
        if (!data.items || data.items.length === 0) {
          throw new Error('No images found in my-images folder');
        }

        // Only images toggled on are available for random selection.
        // If none are enabled (edge case, e.g. legacy data), fall back to most recently added.
        // The server sorts items by createdAt desc, so items[0] is the most recent.
        let selectable = data.items.filter(i => i.enabled !== false);
        if (selectable.length === 0) {
          selectable = [data.items[0]];
        }
        const randomImage = selectable[Math.floor(Math.random() * selectable.length)];
        
        setBackgroundImage(randomImage.url);
        setPhotographer({
          name: 'My Images',
          username: '',
          link: '',
          photoLink: '',
          description: randomImage.name.split('/').pop()
        });

        // No caching - always fetch fresh images

      } else if (theme === 'favorites') {
        // Handle favorites from MongoDB with validation
        const customImage = localStorage.getItem('customBackgroundImage');
        let imageUrl = null;
        let imageTitle = 'From your favorites collection';
        let isValidImage = false;

        if (customImage) {
          console.log('Testing custom favorite image:', customImage);
          isValidImage = isValidImageUrl(customImage) && await testImageLoad(customImage);
          if (isValidImage) {
            imageUrl = customImage;
          } else {
            console.warn('Custom favorite image failed validation or load test');
          }
        }

        // If no custom image or custom image failed, try getting from API
        if (!isValidImage) {
          console.log('Fetching favorites from API...');
          const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/favorites`);
          if (response.ok) {
            const allFavorites = await response.json();
            // Only favorites toggled on are available for random selection.
            // If none are enabled (edge case, e.g. legacy data), fall back to most recently added.
            let favorites = allFavorites.filter(f => f.enabled !== false);
            if (favorites.length === 0 && allFavorites.length > 0) {
              favorites = [allFavorites[0]];
            }
            if (favorites.length > 0) {
              console.log(`Found ${favorites.length} enabled favorites, selecting randomly...`);

              // Shuffle the favorites array to randomize selection
              const shuffledFavorites = [...favorites].sort(() => Math.random() - 0.5);

              // Try each favorite in random order until we find a working one
              for (const favorite of shuffledFavorites) {
                console.log('Testing favorite image:', favorite.title, favorite.imageUrl);
                const isValid = isValidImageUrl(favorite.imageUrl) && await testImageLoad(favorite.imageUrl);
                if (isValid) {
                  imageUrl = favorite.imageUrl;
                  imageTitle = favorite.title;
                  isValidImage = true;
                  console.log('Selected valid favorite:', favorite.title);
                  break;
                }
                console.warn('Favorite image failed validation:', favorite.title);
              }
            }
          }
        }

        if (isValidImage && imageUrl) {
          console.log('Using valid favorite image:', imageUrl);
          setBackgroundImage(imageUrl);
          setPhotographer({
            name: 'Favorites',
            username: '',
            link: '',
            photoLink: imageUrl,
            description: imageTitle
          });
          // No caching - always fetch fresh images
        } else {
          // All favorites failed - use fallback
          console.error('All favorite images failed validation - using fallback');
          const fallbackUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgdmlld0JveD0iMCAwIDgwMCA2MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI4MDAiIGhlaWdodD0iNjAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjQwMCIgeT0iMjgwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNkI3Mjg0IiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiPkZhdm9yaXRlIEltYWdlIE5vdCBGb3VuZDwvdGV4dD4KPHR5ZXh0IHg9IjQwMCIgeT0iMzIwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOUI5QkEwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiPkxpbmsgaGFzIG5vIGltYWdlIG9yIGZhaWxlZCB0byBsb2FkPC90ZXh0Pgo8L3N2Zz4K';
          setBackgroundImage(fallbackUrl);
          setPhotographer({
            name: 'Error',
            username: '',
            link: '',
            photoLink: '',
            description: 'Favorite image link has no image or failed to load'
          });
          // No caching - always fetch fresh images
        }

      } else {
        // Existing Unsplash API call
        const response = await fetch(
          `https://api.unsplash.com/photos/random?query=${theme}&orientation=landscape&content_filter=high&order_by=relevant&featured=true`,
          {
            headers: {
              Authorization: `Client-ID ${process.env.REACT_APP_UNSPLASH_ACCESS_KEY}`
            }
          }
        );
        
        if (!response.ok) throw new Error('Failed to fetch image');
        
        const data = await response.json();
        
        setBackgroundImage(data.urls.full);
        
        setPhotographer({
          name: data.user.name || '',
          username: data.user.username || '',
          link: data.user.links.html || '',
          photoLink: data.links.html || '',
          description: data.description || data.alt_description || ''
        });
        
        // No caching - always fetch fresh images
      }
      
    } catch (error) {
      console.error('Error fetching image:', error);
      setBackgroundImage('/images/test.jpg');
      setPhotographer({
        name: 'Error',
        username: '',
        link: '',
        photoLink: '',
        description: 'Failed to load background image'
      });
    } finally {
      setBackgroundImageReady(true);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    localStorage.removeItem('backgroundImage');
    localStorage.removeItem('photographer');
    localStorage.removeItem('lastImageFetch');
    localStorage.removeItem('customBackgroundImage');
    fetchBackgroundImage(unsplashTheme, true);
  }, [unsplashTheme, isAuthenticated]);

  // Check if current background image is already in favorites
  useEffect(() => {
    const checkIfFavorited = async () => {
      if (!backgroundImage || !isAuthenticated) return;
      try {
        const favorites = await fetchFavorites();
        setFavoritesCache(favorites);
        const match = favorites.find(fav => fav.imageUrl === backgroundImage);
        if (match) {
          setIsFavorited(true);
          setFavoritedId(match._id);
        } else {
          setIsFavorited(false);
          setFavoritedId(null);
        }
      } catch (error) {
        console.error('Error checking favorites:', error);
      }
    };
    checkIfFavorited();
  }, [backgroundImage, isAuthenticated]);

  const handleToggleFavorite = async () => {
    if (isFavorited && favoritedId) {
      // Unfavorite
      try {
        await deleteFavorite(favoritedId);
        setIsFavorited(false);
        setFavoritedId(null);
        setFavoritesCache(prev => prev.filter(f => f._id !== favoritedId));
      } catch (error) {
        console.error('Error removing favorite:', error);
      }
    } else {
      // Favorite - use the current display image URL and caption
      try {
        const title = photographer.description ||
          photographer.name ||
          `${unsplashTheme.charAt(0).toUpperCase() + unsplashTheme.slice(1)} photo`;
        const source = photographer.name === 'Favorites' ? 'custom' : 'unsplash';
        const result = await addFavorite({
          title,
          imageUrl: backgroundImage,
          source,
          tags: [unsplashTheme]
        });
        setIsFavorited(true);
        setFavoritedId(result._id);
        setFavoritesCache(prev => [...prev, result]);
      } catch (error) {
        console.error('Error adding favorite:', error);
      }
    }
  };

  const handleRefreshBackground = () => {
    if (backgroundRefreshCooldown) return;
    refreshBackground();
    setBackgroundRefreshCooldown(true);
    setTimeout(() => setBackgroundRefreshCooldown(false), 3000);
  };

  const handleFreeFlowClick = async () => {
    if (isTransitioningRef.current) return;
    isTransitioningRef.current = true;

    try {
      if (isFreeflow) {
        console.log("Freeflow ended");
        sfxManager.play('sessionEnd');

        // Clean up audio if cleanup function exists
        if (window.audioCleanup) {
          window.audioCleanup();
          window.audioCleanup = null;
        }

        // Only save sessions with non-zero duration
        if (time > 0) {
          const currentSession = JSON.parse(localStorage.getItem('currentSession'));
          // Canonical timestamp = the moment "Begin Session" was clicked. If
          // the ref is somehow null at submit time, that's an unexpected state
          // — fail loud (console + toast) and fall back to derived start so we
          // don't lose the user's session.
          let startedAt = sessionStartTsRef.current;
          if (typeof startedAt !== 'number' || !Number.isFinite(startedAt)) {
            console.error('sessionStartTsRef.current was not a finite number at POST time:', sessionStartTsRef.current);
            toast.error('Session start time was missing — saved with an approximated timestamp.');
            startedAt = Date.now() - time * 1000;
          }
          const newSession = {
            startedAt,
            duration: formatDuration(time),
            text: currentSessionText,
            project: currentSession?.projectId || null
          };

          try {
            // Save to MongoDB
            console.log('Attempting to save session to MongoDB:', newSession);

            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/sessions`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(newSession),
            });

            if (!response.ok) {
              const errorData = await response.json();
              console.error('Server response:', {
                status: response.status,
                statusText: response.statusText,
                error: errorData
              });
              throw new Error(`Failed to save session to MongoDB: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();
            console.log('Session saved to MongoDB:', result);

            // Update local state with the populated session from the response
            const updatedHistory = [...sessionHistory, result];
            setSessionHistory(updatedHistory);
            localStorage.setItem('sessionHistory', JSON.stringify(updatedHistory));
          } catch (error) {
            console.error('Error saving session to MongoDB:', {
              message: error.message,
              stack: error.stack
            });
            // Still update local storage even if MongoDB save fails
            const updatedHistory = [...sessionHistory, newSession];
            setSessionHistory(updatedHistory);
            localStorage.setItem('sessionHistory', JSON.stringify(updatedHistory));
          }
        }

        // First set freeflow to false to trigger fade out animation
        setIsFreeflow(false);
        setTimerActive(false);
        setSessionEnded(true);

        // Wait for fade out animation before unmounting music player
        setTimeout(() => {
          setShowMusicPlayer(false);
          // Only reset session state, not the timer ledger — the displayed time
          // persists until the next "Enter Flow State" begins a new session.
          localStorage.removeItem('currentSession');
          setSessionStarted(false);
          setSessionInputValue('');
          setCurrentSessionText('');
          isTransitioningRef.current = false;
        }, 1000);
      } else {
        console.log("Freeflow started");
        sfxManager.play('enterFlowState');
        // Clear any existing session state AND reset timer when starting new session
        localStorage.removeItem('currentSession');
        setSessionStarted(false);
        setSessionInputValue('');
        setCurrentSessionText('');
        sessionStartTsRef.current = null;
        accumulatedSecondsRef.current = 0;
        lastResumeAtRef.current = null;
        setTimerActive(false);
        setSessionEnded(false);

        // Start new session
        setShowMusicPlayer(true);
        setIsFreeflow(true);

        setTimeout(() => {
          isTransitioningRef.current = false;
        }, 500);
      }
    } catch (err) {
      isTransitioningRef.current = false;
      throw err;
    }
  };

  const handleClearHistory = () => {
    setSessionHistory([]);
    localStorage.removeItem('sessionHistory');
  };

  const handleBeginClick = (inputText, projectId) => {
    // Seed the ledger for a fresh session. The transition useEffect sets lastResumeAtRef
    // when timerActive flips to true.
    sessionStartTsRef.current = Date.now();
    accumulatedSecondsRef.current = 0;
    setTimerActive(true);
    setCurrentSessionText(inputText);
    setSessionInputValue(inputText);
    setSessionStarted(true);

    // Play bell sound (fixed volume, independent of slider)
    sfxManager.play('bell');

    // Save current session state with project (immediate write — bypass throttle)
    const sessionData = {
      sessionStartTimestamp: sessionStartTsRef.current,
      accumulatedSeconds: 0,
      timerActive: true,
      text: inputText,
      projectId,
      isFreeflow,
      showMusicPlayer,
      sessionInputValue: inputText,
      sessionStarted: true
    };
    localStorage.setItem('currentSession', JSON.stringify(sessionData));
    lastStorageWriteRef.current = Date.now();
  };

  const formatDuration = (duration) => {
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleVolumeChange = useCallback((newVolume) => {
    setVolume(newVolume);
  }, []);

  const toggleSessionHistory = () => {
    if (showSessionHistory) {
      setIsSessionHistoryExiting(true);
      setTimeout(() => {
        setShowSessionHistory(false);
        setIsSessionHistoryExiting(false);
      }); // This should match the animation duration in SessionHistory.css
    } else {
      setShowSessionHistory(true);
    }
  };

  const toggleChangeBackgroundImage = () => {
    if (showChangeBackgroundImage) {
      setIsChangeBackgroundImageExiting(true);
    } else {
      setShowChangeBackgroundImage(true);
    }
  };

  const fetchTotalFocusTime = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/total-focus-time`);
      const data = await response.json();
      setTotalFocusedTime(data.totalFocusTime);
    } catch (error) {
      console.error('Error fetching total focus time:', error);
    }
  };

  useEffect(() => {
    console.log('App mounted');
    console.log('Environment:', process.env.NODE_ENV);
    console.log('Public URL:', process.env.PUBLIC_URL);
    // Pre-fetch and decode SFX into AudioBuffers so the first click in a session
    // is zero-latency. AudioContext stays suspended until first user gesture.
    sfxManager.init();
  }, []);

  window.fetchBackgroundImage = fetchBackgroundImage;

  const toggleProfile = () => {
    setShowProfile(prev => !prev);
  };

  const truncateText = (text, limit = 30) => {
    if (!text) return '';
    return text.length > limit ? `${text.slice(0, limit)}...` : text;
  };

  useEffect(() => {
    console.log('Bottom bar rendered:', document.querySelector('.bottom-bar'));
    console.log('Freeflow button rendered:', document.querySelector('.freeflow-button'));
  }, []);

  const toggleTrackList = () => {
    if (showTrackList) {
      setIsTrackListExiting(true);
      setTimeout(() => {
        setShowTrackList(false);
        setIsTrackListExiting(false);
      }, 300); // Match animation duration
    } else {
      setShowTrackList(true);
    }
  };

  useEffect(() => {
    localStorage.setItem('focusPlaylist', JSON.stringify(playlistTracks));
  }, [playlistTracks]);

  // Session restore on mount
  useEffect(() => {
    const savedSession = JSON.parse(localStorage.getItem('currentSession'));
    if (!savedSession) return;

    // Restore ledger refs. Support legacy `{ time: N }` saves: treat as accumulatedSeconds.
    const restoredAccum = savedSession.accumulatedSeconds ?? savedSession.time ?? 0;
    sessionStartTsRef.current = savedSession.sessionStartTimestamp
      ?? (Date.now() - restoredAccum * 1000);
    accumulatedSecondsRef.current = restoredAccum;
    // If session was running when saved, continue wall-clock from now (preserves continuity).
    // If paused, leave lastResumeAt null so derived time stays at accumulated value.
    lastResumeAtRef.current = savedSession.timerActive ? Date.now() : null;
    // Sync transition tracker so the [timerActive] effect doesn't double-handle on first run.
    prevTimerActiveRef.current = !!savedSession.timerActive;

    setTimerActive(!!savedSession.timerActive);
    setCurrentSessionText(savedSession.text || '');
    setIsFreeflow(!!savedSession.isFreeflow);
    setShowMusicPlayer(!!savedSession.showMusicPlayer);
    setSessionInputValue(savedSession.sessionInputValue || '');
    setSessionStarted(!!savedSession.sessionStarted);
  }, []);

  // Separate beforeunload handler effect
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const session = sessionRef.current;
      if (session.timerActive || (session.isFreeflow && session.time > 0) || (session.sessionStarted && !session.sessionEnded)) {
        e.preventDefault();
        e.returnValue = 'You have an active focus session. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []); // No dependencies needed as we use ref

  // Auto-save current session.
  // - While running: throttled heartbeat (≤ once per 5s) so the writes don't pile up.
  // - On every timerActive transition or text/state change: write immediately (bypass throttle).
  // - When no session in progress: clear localStorage.
  useEffect(() => {
    const sessionInProgress = timerActive || sessionStartTsRef.current !== null;
    if (!sessionInProgress) {
      localStorage.removeItem('currentSession');
      return;
    }

    // Throttle heartbeat writes only while actively running. Non-running writes
    // (pause snapshot, text edit, etc.) always go through immediately.
    if (timerActive) {
      const now = Date.now();
      if (now - lastStorageWriteRef.current < 5000) return;
      lastStorageWriteRef.current = now;
    } else {
      lastStorageWriteRef.current = Date.now();
    }

    const existing = JSON.parse(localStorage.getItem('currentSession')) || {};
    localStorage.setItem('currentSession', JSON.stringify({
      sessionStartTimestamp: sessionStartTsRef.current,
      accumulatedSeconds: accumulatedSecondsRef.current,
      timerActive,
      text: currentSessionText,
      projectId: existing.projectId,
      isFreeflow,
      showMusicPlayer,
      sessionInputValue,
      sessionStarted
    }));
  }, [time, timerActive, currentSessionText, isFreeflow, showMusicPlayer, sessionInputValue, sessionStarted]);

  // Add this new helper function near your other formatting functions
  const formatTitleTime = (time) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = time % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
  };

  // Add this new effect to update the document title
  useEffect(() => {
    if (timerActive || time > 0) {
      document.title = `${formatTitleTime(time)} - Flow State`;
    } else {
      document.title = 'Flow State';
    }
  }, [time, timerActive]);

  // Add this function to handle track removal
  const handleRemoveTrack = (trackId) => {
    setPlaylistTracks(currentTracks => {
      // Only allow removal if more than one track remains
      if (currentTracks.length <= 1) {
        // You could show a toast notification here if you want
        return currentTracks;
      }
      return currentTracks.filter(track => track.id !== trackId);
    });
  };

  const toggleQuoteList = () => {
    if (showQuoteList) {
      setIsQuoteListExiting(true);
      setTimeout(() => {
        setShowQuoteList(false);
        setIsQuoteListExiting(false);
      }, 300); // Match animation duration
    } else {
      setShowQuoteList(true);
    }
  };

  const toggleProjects = () => {
    if (showProjects) {
      setIsProjectsExiting(true);
      setTimeout(() => {
        setShowProjects(false);
        setIsProjectsExiting(false);
      }, 300); // Match animation duration
    } else {
      setShowProjects(true);
    }
  };

  const toggleFavoritesPage = () => {
    if (showFavoritesPage) {
      setIsFavoritesPageExiting(true);
      setTimeout(() => {
        setShowFavoritesPage(false);
        setIsFavoritesPageExiting(false);
      }, 300);
    } else {
      setShowFavoritesPage(true);
    }
  };

  const toggleMyImagesPage = () => {
    if (showMyImagesPage) {
      setIsMyImagesPageExiting(true);
      setTimeout(() => {
        setShowMyImagesPage(false);
        setIsMyImagesPageExiting(false);
      }, 300);
    } else {
      setShowMyImagesPage(true);
    }
  };

  const toggleWeatherPage = () => {
    if (showWeatherPage) {
      setIsWeatherPageExiting(true);
      setTimeout(() => {
        setShowWeatherPage(false);
        setIsWeatherPageExiting(false);
      }, 300);
    } else {
      setShowWeatherPage(true);
    }
  };

const loadWeather = useCallback(async () => {
  if (!weatherLocations.length) return;
  const results = await Promise.all(
    weatherLocations.map(async (loc) => {
      try {
        const data = await fetchWeather(loc.lat, loc.lon, weatherUnit);
        return {
          city: data.name,
          temp: data.main.temp,
          iconKey: getWeatherIconKey(data.weather[0].id),
          isNight: getIsNight(data.weather[0].icon),
        };
      } catch { return null; }
    })
  );
  setWeatherDataList(results);
}, [weatherLocations, weatherUnit]);

  useEffect(() => {
    if (!weatherLocations.length) return;
    loadWeather();
    const interval = setInterval(loadWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [weatherLocations, weatherUnit, loadWeather]);

  const saveLocations = (locations) => {
    setWeatherLocations(locations);
    localStorage.setItem('weatherLocations', JSON.stringify(locations));
    localStorage.removeItem('weatherLocation');
  };

  const handleAddWeatherLocation = (locationObj, data) => {
    if (weatherLocations.length >= 3) return;
    const next = [...weatherLocations, locationObj];
    saveLocations(next);
    if (data) setWeatherDataList(prev => [...prev, data]);
  };

  const handleRemoveWeatherLocation = (index) => {
    const next = weatherLocations.filter((_, i) => i !== index);
    saveLocations(next);
    setWeatherDataList(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditWeatherLocation = (index, locationObj, data) => {
    const next = weatherLocations.map((loc, i) => i === index ? locationObj : loc);
    saveLocations(next);
    if (data) setWeatherDataList(prev => prev.map((d, i) => i === index ? data : d));
  };

  const handleSetWeatherUnit = (newUnit) => {
    setWeatherUnit(newUnit);
    localStorage.setItem('weatherUnit', newUnit);
  };

  if (isLoading || (isAuthenticated && !backgroundImageReady)) {
    return (
      <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-900">
        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-white tw-border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <div className="grid-container">
      <div 
        className="background" 
        style={{ backgroundImage: `url(${backgroundImage})` }}
      ></div>
      {photographer.name && photographer.name !== 'My Images' && (
        <div className="photo-attribution">
          <div className="tw-flex tw-items-start tw-justify-end tw-gap-2">
            <button
              onClick={handleRefreshBackground}
              disabled={isRefreshingBackground || backgroundRefreshCooldown}
              title="Refresh background image"
              className="favorite-heart-btn"
            >
              <ArrowsClockwise
                size={16}
                weight="regular"
                className={`tw-text-white tw-transition-transform ${isRefreshingBackground ? 'tw-animate-spin' : ''}`}
              />
            </button>
            <a
              href={photographer.photoLink}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-flex-1"
            >
              <p className="tw-text-right">
                {truncateText(
                  photographer.description ||
                  `${unsplashTheme.charAt(0).toUpperCase() + unsplashTheme.slice(1)} photo`
                )}
              </p>
            </a>
            <button
              onClick={handleToggleFavorite}
              title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
              className="favorite-heart-btn"
            >
              <Heart
                size={16}
                weight={isFavorited ? 'fill' : 'regular'}
                className={`tw-transition-all tw-duration-200 ${isFavorited ? 'tw-text-red-500' : 'tw-text-white'}`}
              />
            </button>
          </div>
          <p>
            Photo by{' '}
            <a
              href={photographer.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {photographer.name}
            </a>
            {' '}on{' '}
            <a
              href="https://unsplash.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Unsplash
            </a>
          </p>
        </div>
      )}
      <div className="app">
        <div className="tw-fixed tw-top-0 tw-left-0 tw-z-50 tw-flex tw-flex-row tw-items-center">
          <button
            className="tw-p-4 tw-cursor-pointer tw-transition-all tw-duration-200 tw-ease-in-out tw-bg-transparent tw-border-0"
            onClick={() => setIsMenuOpen(true)}
          >
            <div className="tw-space-y-2">
              <span className="tw-block tw-h-0.5 tw-w-5 tw-bg-white"></span>
              <span className="tw-block tw-h-0.5 tw-w-8 tw-bg-white"></span>
              <span className="tw-block tw-h-0.5 tw-w-8 tw-bg-white"></span>
            </div>
          </button>
          <div className="weather-scroll tw-flex tw-flex-row tw-items-center">
            {weatherLocations.map((_, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="tw-block tw-w-px tw-h-6 tw-bg-white tw-opacity-20 tw-self-center" />}
                <WeatherWidget weatherData={weatherDataList[i] || null} onRefresh={loadWeather} />
              </React.Fragment>
            ))}
          </div>
        </div>
        <Menu 
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          onProfileClick={() => {
            setIsMenuOpen(false);
            toggleProfile();
          }}
          onShowHistory={() => {
            setIsMenuOpen(false);
            toggleSessionHistory();
          }}
          onBackgroundImage={() => {
            setIsMenuOpen(false);
            toggleChangeBackgroundImage();
          }}
          onTrackList={() => {
            setIsMenuOpen(false);
            toggleTrackList();
          }}
          onQuoteList={() => {
            setIsMenuOpen(false);
            toggleQuoteList();
          }}
          onProjects={() => {
            setIsMenuOpen(false);
            toggleProjects();
          }}
        />
        {showMusicPlayer && (
          <MusicPlayer
            isFreeflow={isFreeflow}
            onBeginClick={handleBeginClick}
            stopAudio={setTimerActive}
            setTimerActive={setTimerActive}
            volume={volume}
            onVolumeChange={handleVolumeChange}
            playlistTracks={playlistTracks}
            sessionInputValue={sessionInputValue}
            setSessionInputValue={setSessionInputValue}
            sessionStarted={sessionStarted}
            setSessionStarted={setSessionStarted}
          />
        )}
        <HandleTimer
          time={time}
          position={sessionStarted ? 'in-player' : isFreeflow ? 'top' : 'center'}
          showLabel={!showMusicPlayer}
        />
        <nav className="bottom-navbar">
          <button className="freeflow-button" onClick={handleFreeFlowClick}>
            {isFreeflow ? "Exit Flow State" : "Enter Flow State"}
          </button>
        </nav>
      </div>
      {(showSessionHistory || isSessionHistoryExiting) && (
        <SessionHistoryPage
          sessionHistory={sessionHistory}
          onClearHistory={handleClearHistory}
          onClose={toggleSessionHistory}
          totalFocusedTime={totalFocusedTime}
          isExiting={isSessionHistoryExiting}
        />
      )}
      <Profile
        isOpen={showProfile}
        onClose={toggleProfile}
        onFavoritesClick={() => {
          toggleProfile();
          toggleFavoritesPage();
        }}
        onMyImagesClick={() => {
          toggleProfile();
          toggleMyImagesPage();
        }}
        onWeatherClick={() => {
          toggleProfile();
          toggleWeatherPage();
        }}
      />
      {showChangeBackgroundImage && (
        <ChangeBackground
          onClose={() => {
            setIsChangeBackgroundImageExiting(true);
            setTimeout(() => {
              setShowChangeBackgroundImage(false);
              setIsChangeBackgroundImageExiting(false);
            }, 300);
          }}
          theme={unsplashTheme}
          setTheme={setUnsplashTheme}
          isExiting={isChangeBackgroundImageExiting}
          fetchBackgroundImage={fetchBackgroundImage}
        />
      )}
      {(showTrackList || isTrackListExiting) && (
        <TrackListPage
          onClose={toggleTrackList}
          isExiting={isTrackListExiting}
          playlistTracks={playlistTracks}
          setPlaylistTracks={setPlaylistTracks}
          onRemoveTrack={handleRemoveTrack}
        />
      )}
      {(showQuoteList || isQuoteListExiting) && (
        <QuoteList
          onClose={toggleQuoteList}
          isExiting={isQuoteListExiting}
        />
      )}
      {(showProjects || isProjectsExiting) && (
        <Projects
          onClose={toggleProjects}
          isExiting={isProjectsExiting}
        />
      )}
      {(showFavoritesPage || isFavoritesPageExiting) && (
        <FavoritesPage
          onClose={toggleFavoritesPage}
          isExiting={isFavoritesPageExiting}
        />
      )}
      {(showMyImagesPage || isMyImagesPageExiting) && (
        <MyImagesPage
          onClose={toggleMyImagesPage}
          isExiting={isMyImagesPageExiting}
        />
      )}
      {(showWeatherPage || isWeatherPageExiting) && (
        <WeatherPage
          onClose={toggleWeatherPage}
          isExiting={isWeatherPageExiting}
          locations={weatherLocations}
          unit={weatherUnit}
          onAddLocation={handleAddWeatherLocation}
          onRemoveLocation={handleRemoveWeatherLocation}
          onEditLocation={handleEditWeatherLocation}
          onSetUnit={handleSetWeatherUnit}
        />
      )}
    </div>
  );
}

export default App;